import AppButton from '@nxsys/components/AppButton';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { rpnActions } from '@nxsys/store/slices/rpn/reducer/rpnSlice';
import {
  GetEmployeeListToUpdateRPNParams
} from '@nxsys/store/slices/rpn/types';
// import { Icons } from '@nxsys/types/icons';
import { useUpdateAllAEPNApi } from '@nxsys/hooks/api/aepn';
import useAEPNSocket from '@nxsys/hooks/useAEPNSocket';
import { useMobile } from '@nxsys/hooks/useMobile';
import { Icons } from '@nxsys/types/icons';
import { showMessage } from '@nxsys/utils/message';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';
import '../styles.css';

type UpdateAEPNModalProps = {
  isOpen: boolean;
  toggle: () => void;
  cbAfterAction?: () => void;
};

const UpdateAEPNModal: React.FC<UpdateAEPNModalProps> = ({
  isOpen,
  toggle,
  cbAfterAction,
}: UpdateAEPNModalProps) => {
  const { messages } = useTypedIntl();
  const { isMobile } = useMobile();
  const navigate = useNavigateWithQuery();

  const dispatch = useDispatch<AppDispatch>();
  const { isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );

  const { employeeList } = useSelector((state: AppState) => state.rpnStore);
  const { loading, list, pagination, queryParams } = employeeList;

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      paymentModel: 'PAYEE',
      status: 'ACTIVE',
      appName: 'BUREAU',
    });
  }, []);

  const handleFetchData = (params: GetEmployeeListToUpdateRPNParams) => {
    const newParams = { ...params };
    newParams.companyIds = resolveFilterCompanyIds(
      newParams.companyIds,
      isBureauEmployer,
      user?.companyUserIds,
    );
    dispatch(rpnActions.getEmployeeListToUpdateRPN(newParams));
  };

  const refetchData = () => {
    handleFetchData({ ...queryParams });
    cbAfterAction?.();
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: ColumnsType<any> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="table-actions flex items-center justify-start">
          <Tooltip title="View">
            <li
              onClick={() =>
                navigate(`/employees-subcontractors/${record.id}`, {
                  query: { from: `/aepn` },
                })
              }
            >
              <Icons.IconEye />
            </li>
          </Tooltip>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'id',
      title: 'Employee ID',
      dataIndex: 'id',
    },
    {
      key: 'employeeName',
      title: 'Employee Name',
      render: (record) =>
        record?.fullName ?? `${record?.forename} ${record?.surname}`,
    },
    {
      key: 'agencyRef',
      title: messages['common.employerRef'],
      dataIndex: 'agencyRef',
    },
    {
      key: 'companyPayment',
      title: 'Employer',
      dataIndex: 'companyPayment',
      render: (record) => record?.companyName ?? '',
    },
    {
      key: 'errorMessage',
      title: messages['rpn.errorMessage'],
      width: 400,
      render: (record) => {
        const getMessage = () => {
					if (!record?.currentAepn) {
						return 'The employee has no Auto Enrolment';
					}
					if (
						moment(record?.currentAepn?.createdAt).format('DD/MM/YYYY') !==
						moment().format('DD/MM/YYYY')
					) {
						return 'Auto Enrolment was not updated today';
					}
					return '_';
				};
        return getMessage();
      },
    ...(!isMobile ? { fixed: 'right' } : {}),
  },
  ];

  const getRowClassName = (record: any) => {
    if (!record?.currentAepn || moment(record?.currentAepn?.createdAt).format('DD/MM/YYYY') !== moment().format('DD/MM/YYYY')) {
			return 'row-warning';
		}
    return '';
  };

  const { run: updateAllAEPN, loading: isUpdatingAEPN } = useUpdateAllAEPNApi();
  const handleUpdateRPNAll = async () => {
    const res = await updateAllAEPN();
    if (res.success) {
      showMessage({
        type: 'info',
        content: 'Starting update AEPN. Please wait...',
      });
    } else {
      showMessage({
        type: 'error',
        content: res.message || 'Fail to start update AEPN',
      });
    }
  };
  useAEPNSocket({
    eventName: `naersa-aepn-download-all-${user?.id}`,
    onSuccess: (data) => {
      if (data?.success) {
				// Check for errors in results
				const errors = data?.results?.filter((r: any) => r.errorMessage) || [];
				const success = data?.results?.filter((r: any) => !r.errorMessage) || [];
				if (errors.length > 0) {
					const errorMessages = errors.map((e: any) => `${e.companyId}: ${e.errorMessage}`).join(', ');
					showMessage({
						type: 'error',
						content: `Update AEPN failed: ${errorMessages}`,
					});
					if (success.length > 0) {
						const companyIds = success.map((s: any) => s.companyId).join(', ');
						showMessage({
							type: 'success',
							content: `Update AEPN successfully for employers: ${companyIds}`,
						});
					}
				} else {
					showMessage({
						type: 'success',
						content: 'Update AEPN successfully',
					});
				}
				refetchData();
			} else {
				showMessage({
					type: 'error',
					content: data?.message || 'Fail to update AEPN.',
				});
			}
    },
    onError: (error) => {
      showMessage({
        type: 'error',
        content: error || 'Fail to update AEPN',
      });
    },
  });

  return (
    <AppModalContainer
      title="Update AEPN"
      isOpen={isOpen}
      toggle={toggle}
      width={'90vw'}
    >
      <AppsHeader>
        <div className="flex justify-end w-full">
          <AppButton name='Update All' onClick={handleUpdateRPNAll} loading={isUpdatingAEPN} disabled={isUpdatingAEPN} />
        </div>
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1500, y: 700 }}
          titleEmpty={messages['rpn.employeeList.empty']}
          columns={columns}
          onChangePagination={onChangePagination}
          rowClassName={getRowClassName}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default UpdateAEPNModal;
